import { epiCycles } from "./epicycles.js";
import pFiveSketch from "./pFiveSketch.js";
import { pathfinderSVG } from "./findPath.js";
import { analyse, reconstructPath, setSource, state } from "./signal.js";
import { colors } from "./theme.js";
import * as p5 from "p5";

export const BASE = new pFiveSketch();
const parser = new DOMParser();

//Layout of the canvas: the x chain runs below the drawing and the y chain on its right.
const WIDTH = 700;
const HEIGHT = 600;
const MARGIN = 16;
const GAP = 24;
const HALF_WIDTH = 200; //Largest size of the drawing, before making room for the chains.
const HALF_HEIGHT = 180;
const MAX_POINTS = 1500; //Keeps the O(N^2) transform and the redraws fast.
const STEPS = 400; //Frames needed to draw the whole path.

export function imageHandler(name, label, svgData) {
  //get path tags of svg string.
  const doc = parser.parseFromString(svgData, "image/svg+xml");
  const pathTags = doc.getElementsByTagName("path");

  var imFactor;
  switch (name) {
    case "deer":
      imFactor = 4;
      break;

    case "epl":
    case "tux":
    case "dragon":
      imFactor = 3;
      break;

    default:
      throw new Error("Error, no match found for the base image selected.");
  }

  //As we have too many values we only keep one point every 2 * imFactor units of length.
  let pathSketch = pathfinderSVG(pathTags, 2 * imFactor)[0];
  const skip = Math.ceil(pathSketch.length / MAX_POINTS);
  pathSketch = pathSketch.filter((p, i) => i % skip === 0);

  let points = fitToBox(pathSketch);
  let signal = analyse(label, points);
  //Shrinks the drawing so that its largest epicycles fit in the canvas too (the transform is linear).
  const scale = Math.min(1, ...availableRatio(signal));
  if (scale < 1) {
    points = points.map((p) => ({ x: p.x * scale, y: p.y * scale }));
    signal = analyse(label, points);
  }
  setSource("example", signal);
  const { fourierX, fourierY } = signal;
  const { centerX, centerY, chainX, chainY } = layout(signal);
  const toCanvas = (p) => ({ x: centerX + p.x, y: centerY - p.y });
  const numSamples = Math.min(Math.max(2 * signal.numPoints, 400), 2000);

  let baseSketch = function (pFIVE) {
    let time = 0;
    let path = [];
    let pathK = null;

    pFIVE.setup = function () {
      let cnv = pFIVE.createCanvas(WIDTH, HEIGHT);
      cnv.parent("startup");
      cnv.id("baseCanvas");
      pFIVE.frameRate(30);
    };

    //Draws pictuer.
    pFIVE.draw = function () {
      const color = colors();
      const K = state.K;
      if (K !== pathK) {
        //Path drawn by the K largest terms of each coordinate.
        path = reconstructPath(signal, K, numSamples).map(toCanvas);
        pathK = K;
      }
      pFIVE.background(color.surface);
      pFIVE.noFill();

      const original = document.getElementById("fx-show-original");
      if (original === null || original.checked) {
        pFIVE.stroke(color.faint);
        pFIVE.strokeWeight(2);
        pFIVE.beginShape();
        for (let i = 0; i < signal.numPoints; i++) {
          const p = toCanvas({ x: signal.x[i], y: signal.y[i] });
          pFIVE.vertex(p.x, p.y);
        }
        pFIVE.endShape(pFIVE.CLOSE);
      }

      //Create the 2 epicycle generaters. The x chain reads x(t) horizontally, the y chain is turned by -pi/2 to read y(t) vertically.
      let vx = epiCycles(pFIVE, fourierX.terms, K, time, centerX + fourierX.mean, chainY, 0, color.x); //Vector x
      let vy = epiCycles(pFIVE, fourierY.terms, K, time, chainX, centerY - fourierY.mean, -pFIVE.HALF_PI, color.y); //Vector y.
      let v = pFIVE.createVector(vx.x, vy.y); //As we want coordinate from fourierX and y from fourierY.
      pFIVE.stroke(color["ink-muted"]);
      pFIVE.line(vx.x, vx.y, v.x, v.y); //Draw the x and y of drawing.
      pFIVE.line(vy.x, vy.y, v.x, v.y);

      //Draw the part of the path already traced.
      const done = time >= pFIVE.TWO_PI;
      const last = done ? path.length : Math.floor((time / pFIVE.TWO_PI) * path.length) + 1;
      pFIVE.stroke(color.ink);
      pFIVE.strokeWeight(2);
      pFIVE.beginShape();
      for (let i = 0; i < last; i++) {
        pFIVE.vertex(path[i].x, path[i].y);
      }
      if (!done) {
        pFIVE.vertex(v.x, v.y);
      }
      pFIVE.endShape(done ? pFIVE.CLOSE : undefined);

      time += pFIVE.TWO_PI / STEPS; //Amount of time I move each frame of animatoon.

      //This resets drawing when we complete it.
      if (time > pFIVE.TWO_PI * 2) {
        time = 0;
      }
    };
  };
  BASE.sketchP5 = new p5(baseSketch);
}

/**
 * @function fitToBox Scales the path to the largest drawing box. Returns its points from the centre of the box, y pointing up.
 */
function fitToBox(points) {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const scale = Math.min((2 * HALF_WIDTH) / (maxX - minX || 1), (2 * HALF_HEIGHT) / (maxY - minY || 1));
  return points.map((p) => ({
    x: (p.x - (minX + maxX) / 2) * scale,
    y: ((minY + maxY) / 2 - p.y) * scale,
  }));
}

/**
 * @function extent Sizes to fit: the half sizes of the drawing and the radii of the first (largest) circle of each chain.
 */
function extent(signal) {
  const { x, y, fourierX, fourierY } = signal;
  return {
    halfWidth: (Math.max(...x) - Math.min(...x)) / 2,
    halfHeight: (Math.max(...y) - Math.min(...y)) / 2,
    radiusX: fourierX.terms.length ? fourierX.terms[0].amp : 0,
    radiusY: fourierY.terms.length ? fourierY.terms[0].amp : 0,
  };
}

function availableRatio(signal) {
  const { halfWidth, halfHeight, radiusX, radiusY } = extent(signal);
  return [
    (WIDTH - 2 * MARGIN - GAP) / (2 * halfWidth + 2 * radiusY || 1),
    (HEIGHT - 2 * MARGIN - GAP) / (2 * halfHeight + 2 * radiusX || 1),
  ];
}

/**
 * @function layout Centres the drawing, the x chain under it and the y chain on its right in the canvas.
 */
function layout(signal) {
  const { halfWidth, halfHeight, radiusX, radiusY } = extent(signal);
  const left = (WIDTH - (2 * halfWidth + GAP + 2 * radiusY)) / 2;
  const top = (HEIGHT - (2 * halfHeight + GAP + 2 * radiusX)) / 2;
  return {
    centerX: left + halfWidth,
    centerY: top + halfHeight,
    chainX: left + 2 * halfWidth + GAP + radiusY,
    chainY: top + 2 * halfHeight + GAP + radiusX,
  };
}
